/**
 * Ansprechpartner eines Geschäftspartners.
 *
 * Ein Kunde kann mehrere haben — Einkauf, Bauleitung, Buchhaltung. Der
 * als Vorgabe markierte wird bei neuen Belegen vorbelegt; auf dem Beleg
 * erscheint er in der z.-Hd.-Zeile und in der Briefanrede.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { contactDisplayName } from '@erp/shared';

import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from './toast';
import { Badge, Dialog, EmptyState, Field, LoadingBlock, TextInput } from './ui';

export type PartnerContact = {
  id: string;
  partnerId: string;
  salutation: string | null;
  firstName: string | null;
  lastName: string;
  position: string | null;
  department: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  isPrimary: boolean;
  notes: string | null;
};

export function PartnerContacts({ partnerId }: { partnerId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayWrite = can('masterdata:write');

  const [editing, setEditing] = useState<PartnerContact | 'neu' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['partner-contacts', partnerId],
    queryFn: () => api.get<{ contacts: PartnerContact[] }>(`/api/partners/${partnerId}/contacts`),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/partners/${partnerId}/contacts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-contacts', partnerId] });
      toast.success('Der Ansprechpartner wurde gelöscht.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.'),
  });

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2>Ansprechpartner</h2>
          <div className="faint" style={{ fontSize: 12.5 }}>
            Erscheinen auf Belegen in Anrede und z.-Hd.-Zeile — die Vorgabe wird vorbelegt
          </div>
        </div>
        {mayWrite ? (
          <button type="button" className="btn btn-sm" onClick={() => setEditing('neu')}>
            Ansprechpartner hinzufügen
          </button>
        ) : null}
      </div>

      {isLoading ? <LoadingBlock /> : null}

      {data ? (
        data.contacts.length === 0 ? (
          <EmptyState
            title="Keine Ansprechpartner hinterlegt"
            hint="Belege tragen dann keine persönliche Anrede."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Funktion</th>
                  <th>Telefon</th>
                  <th>E-Mail</th>
                  <th className="shrink"></th>
                  {mayWrite ? <th className="shrink"></th> : null}
                </tr>
              </thead>
              <tbody>
                {data.contacts.map((contact) => (
                  <tr key={contact.id}>
                    <td style={{ fontWeight: 500 }}>{contactDisplayName(contact)}</td>
                    <td className="muted">
                      {[contact.position, contact.department].filter(Boolean).join(' · ') || ''}
                    </td>
                    <td className="muted">
                      {[contact.phone, contact.mobile].filter(Boolean).join(' · ') || ''}
                    </td>
                    <td className="muted">{contact.email ?? ''}</td>
                    <td>{contact.isPrimary ? <Badge kind="accent">Vorgabe</Badge> : null}</td>
                    {mayWrite ? (
                      <td>
                        <div className="row" style={{ gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setEditing(contact)}
                          >
                            Ändern
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => remove.mutate(contact.id)}
                          >
                            Löschen
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {editing ? (
        <ContactDialog
          partnerId={partnerId}
          contact={editing === 'neu' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function ContactDialog({
  partnerId,
  contact,
  onClose,
}: {
  partnerId: string;
  contact: PartnerContact | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    salutation: contact?.salutation ?? '',
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    position: contact?.position ?? '',
    department: contact?.department ?? '',
    phone: contact?.phone ?? '',
    mobile: contact?.mobile ?? '',
    email: contact?.email ?? '',
    isPrimary: contact?.isPrimary ?? false,
    notes: contact?.notes ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        salutation: form.salutation || null,
        firstName: form.firstName || null,
        position: form.position || null,
        department: form.department || null,
        phone: form.phone || null,
        mobile: form.mobile || null,
        email: form.email || null,
        notes: form.notes || null,
      };
      return contact
        ? api.patch(`/api/partners/${partnerId}/contacts/${contact.id}`, payload)
        : api.post(`/api/partners/${partnerId}/contacts`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-contacts', partnerId] });
      toast.success('Gespeichert.');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        if (Object.keys(error.fieldErrors).length === 0) toast.error(error.message);
      } else {
        toast.error('Speichern fehlgeschlagen.');
      }
    },
  });

  return (
    <Dialog
      title={contact ? 'Ansprechpartner ändern' : 'Ansprechpartner hinzufügen'}
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || !form.lastName.trim()}
            onClick={() => {
              setErrors({});
              save.mutate();
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field
          label="Anrede"
          span={3}
          error={errors.salutation}
          hint={'„Herr" oder „Frau" steuert die Briefanrede'}
        >
          <TextInput
            value={form.salutation}
            onChange={(value) => setForm({ ...form, salutation: value })}
            placeholder="Herr / Frau / Herr Dr. …"
          />
        </Field>
        <Field label="Vorname" span={4} error={errors.firstName}>
          <TextInput
            value={form.firstName}
            onChange={(value) => setForm({ ...form, firstName: value })}
          />
        </Field>
        <Field label="Nachname" span={5} required error={errors.lastName}>
          <TextInput
            value={form.lastName}
            onChange={(value) => setForm({ ...form, lastName: value })}
            invalid={Boolean(errors.lastName)}
            autoFocus
          />
        </Field>

        <Field label="Funktion" span={6} error={errors.position} hint="z. B. Einkauf, Bauleitung">
          <TextInput
            value={form.position}
            onChange={(value) => setForm({ ...form, position: value })}
          />
        </Field>
        <Field label="Abteilung" span={6} error={errors.department}>
          <TextInput
            value={form.department}
            onChange={(value) => setForm({ ...form, department: value })}
          />
        </Field>

        <Field label="Telefon" span={4} error={errors.phone}>
          <TextInput
            value={form.phone}
            onChange={(value) => setForm({ ...form, phone: value })}
            type="tel"
          />
        </Field>
        <Field label="Mobil" span={4} error={errors.mobile}>
          <TextInput
            value={form.mobile}
            onChange={(value) => setForm({ ...form, mobile: value })}
            type="tel"
          />
        </Field>
        <Field label="E-Mail" span={4} error={errors.email}>
          <TextInput
            value={form.email}
            onChange={(value) => setForm({ ...form, email: value })}
            type="email"
          />
        </Field>

        <Field label="Notizen" span={12} error={errors.notes}>
          <TextInput value={form.notes} onChange={(value) => setForm({ ...form, notes: value })} />
        </Field>
      </div>

      <label className="checkbox-row" style={{ marginTop: 14 }}>
        <input
          type="checkbox"
          checked={form.isPrimary}
          onChange={(event) => setForm({ ...form, isPrimary: event.target.checked })}
        />
        Als Vorgabe verwenden — wird bei neuen Belegen automatisch eingesetzt
      </label>
    </Dialog>
  );
}
